import React, { useState } from 'react';
import { setDocKind } from './api';

const OPEN_STATES = ['needs_review', 'awaiting_approval'];

const MATCH_TEXT = {
  exact: (m) => `It settles the supplier claim ${m.matched_document_id} exactly.`,
  partial: (m) => `It part-settles the supplier claim ${m.matched_document_id}; ${String(m.variance || '').replace('-', '')} is still owed.`,
  over: (m) => `It is more than the supplier claim ${m.matched_document_id}; check it before relying on the match.`,
  ambiguous: () => 'More than one waiting supplier claim could be this credit; it was not matched to any.',
  unmatched: () => 'No waiting supplier claim matches this credit.',
};

/**
 * Whether the document is an invoice or a supplier credit note (backend/credit_note.py). A credit note moves money and
 * nothing else: approving it sets no price and adds no stock, and it is matched to the supplier claims still waiting for a
 * credit. The server decides from facts (a negative total, "credit note" in the email subject or file name, a CN number)
 * and says which; a person who can decide may overrule it until it is decided.
 */
export default function DocKind({ invoice, canDecide = true, onChanged }) {
  const credit = invoice.doc_kind === 'credit_note';
  const open = OPEN_STATES.includes(invoice.state);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const change = async (kind) => {
    setBusy(true); setError(null);
    try { await setDocKind(invoice.id, kind); onChanged?.(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!credit) {
    if (!(open && canDecide)) return null;
    return (
      <p className="text-sm text-ink-muted" data-testid="doc-kind-invoice">
        <button type="button" disabled={busy} onClick={() => change('credit_note')} data-testid="doc-kind-make-credit"
          className="underline">This is a credit note, not an invoice</button>
        {error && <span className="ml-2 text-danger" data-testid="doc-kind-error">{error}</span>}
      </p>
    );
  }
  const match = invoice.credit_match;
  return (
    <section className="rounded-2xl border border-accent-300 bg-accent-50 p-4 space-y-2" data-testid="doc-kind-credit">
      <h2 className="font-display text-lg text-ink">Credit note</h2>
      <p className="text-sm text-ink" data-testid="doc-kind-effect">
        Approving a credit note sets no price and adds no stock. Stock comes back by a count, or by voiding the invoice it
        corrects.
      </p>
      {(invoice.doc_kind_signals || []).length > 0 && (
        <p className="text-xs text-ink-muted" data-testid="doc-kind-signals">
          Read as a credit note because of: {invoice.doc_kind_signals.join(', ')}.
        </p>
      )}
      {match && (
        <p className="text-sm text-ink" data-testid="doc-kind-match">{(MATCH_TEXT[match.state] || MATCH_TEXT.unmatched)(match)}</p>
      )}
      {open && canDecide && (
        <button type="button" disabled={busy} onClick={() => change('invoice')} data-testid="doc-kind-make-invoice"
          className="min-h-[44px] px-4 rounded-xl border border-surface-border text-ink hover:bg-surface-sunken">
          It is an invoice
        </button>
      )}
      {error && <p className="text-danger" data-testid="doc-kind-error">{error}</p>}
    </section>
  );
}
